import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { SALES_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL FUNCTIONS
import { makePath } from 'functions.js';

// HOOKS
import useRecordView from 'helpers/getRecordView';
import { useRecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import UploadButton from 'core/form/control/types/upload/parts/UploadButton';

// LOCAL COMPONENTS
import { WARNING_MESSAGE } from 'routers/interface/tools/attachment/parts/AttachmentUpload';

// MAIN COMPONENT
const AttachmentButton = ({
  index,
  className,
  label,
  icon,
  link,
  outline,
  fullWidth
}) => {

  // FORMIK
  const { values: { aryDownPayment = [] }, initialValues, setFieldValue } = useFormikContext();

  // HOOKS
  const { isAdd, isView } = useRecordView();
  const { record, recordID } = useRecordContext() || {};

  // MEMOS
  const downPaymentId = useMemo(
    () => aryDownPayment[index]?.down_payment_id,
    [index, aryDownPayment]
  )

  // CALLBACKS
  const handleUpload = useCallback(
    () => {
      if (!isAdd && downPaymentId !== '') setFieldValue(`aryDownPayment[${index}].isUpdated`, true);
    },
    [index, downPaymentId, setFieldValue, isAdd]
  )

  const handleUploadMeta = useCallback(data => ({
    temp: !!data.temp_attachment_id,
    attachmentId: data.dp_attachment_id || data.temp_attachment_id || data.attachmentId,
    fileName: data.attachment_filename || data.fileName,
    uploadedBy: data.updated_by || data.created_by || data.uploadedBy,
    uploadedOn: data.date_updated || data.date_created || data.uploadedOn || data.timecreated
  }), []);

  // If it's a newly added Down Payment then okay to use temp endpoint
  const isNewDownPayment = !initialValues?.aryDownPayment?.[index];

  // RENDER
  return (
    <UploadButton
      label="Down Payment"
      uploadArgs={(_uploadID, file) => ({
        endpoint: !isView && isNewDownPayment
          ? ENDPOINTS.uploads.sales.temp
          : makePath(ENDPOINTS.uploads.sales.upload, record.sales_id),
        params: isAdd ? {
          dailySalesInventoryId: record.daily_sales_inventory_id,
          documentType: SALES_FILE_TYPE_IDS.INVOICE_DOWN_PAYMENT,
          fileName: file.name
        } : {
          documentType: SALES_FILE_TYPE_IDS.INVOICE_DOWN_PAYMENT,
          downPaymentId,
          fileName: file.name,
          salesId: recordID
        }
      })}
      uploadMeta={handleUploadMeta}
      uploadFieldValue={data => ({
        temp_attachment_id: data.temp_attachment_id,
        dp_attachment_id:  data.dp_attachment_id || data.attachmentId,
        attachment_filename: data.fileName,
        created_by_id: data.created_by_site_user_id,
        created_by: data.uploadedBy,
        date_created: data.timecreated,
        down_payment_id: downPaymentId
      })}
      multiple
      onUpload={handleUpload}
      button={{
        className,
        label,
        icon,
        link,
        outline,
        fullWidth
      }}
      modal={{
        message: WARNING_MESSAGE
      }}
    />
  )
}

export default AttachmentButton;
